import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileSpreadsheet, Upload, FileDown, CheckCircle2, AlertTriangle, Database } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatDateID } from '../lib/format';

const IMPORT_TYPES = [
  { key: 'clients', label: 'Client', file: 'template-clients.xlsx' },
  { key: 'products', label: 'Produk / Layanan', file: 'template-products.xlsx' },
  { key: 'packages', label: 'Paket', file: 'template-packages.xlsx' },
  { key: 'package_items', label: 'Item Paket', file: 'template-package-items.xlsx' },
  { key: 'quotations', label: 'Penawaran', file: 'template-quotations.xlsx' },
  { key: 'quotation_items', label: 'Line Items Penawaran', file: 'template-quotation-items.xlsx' },
  { key: 'invoices', label: 'Invoice', file: 'template-invoices.xlsx' },
  { key: 'invoice_items', label: 'Line Items Invoice', file: 'template-invoice-items.xlsx' },
  { key: 'payments', label: 'Pembayaran', file: 'template-payments.xlsx' },
  { key: 'followups', label: 'Follow-up', file: 'template-followups.xlsx' },
];

const EXPORT_OPTIONS = [
  { key: 'clients', label: 'Semua Client', url: '/export/clients?format=xlsx', file: 'client.xlsx' },
  { key: 'products', label: 'Semua Produk', url: '/export/products?format=xlsx', file: 'produk.xlsx' },
  { key: 'packages', label: 'Semua Paket', url: '/export/packages?format=xlsx', file: 'paket.xlsx' },
  { key: 'quotations', label: 'Semua Penawaran', url: '/export/quotations?format=xlsx', file: 'penawaran.xlsx' },
  { key: 'invoices', label: 'Semua Invoice', url: '/export/invoices?format=xlsx', file: 'invoice.xlsx' },
  { key: 'payments', label: 'Semua Pembayaran', url: '/export/payments?format=xlsx', file: 'pembayaran.xlsx' },
  { key: 'receivables', label: 'Semua Piutang', url: '/export/receivables?format=xlsx', file: 'piutang.xlsx' },
  { key: 'overdue', label: 'Piutang Overdue', url: '/export/receivables?overdue_only=true&format=xlsx', file: 'piutang-overdue.xlsx' },
  { key: 'paid', label: 'Invoice Lunas', url: '/export/invoices?status=Paid&format=xlsx', file: 'invoice-lunas.xlsx' },
];

const downloadFile = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const blob = res.data;
    const link = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = link; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(link);
    toast.success('Berkas diunduh');
  } catch (e) {
    toast.error(e?.response?.data?.detail || 'Gagal mengunduh');
  }
};

export default function ImportExport() {
  const [logs, setLogs] = useState([]);
  const [importType, setImportType] = useState('clients');
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('create');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const loadLogs = async () => {
    try {
      const { data } = await api.get('/import/logs');
      setLogs(data);
    } catch (err) {
      // Endpoint is RBAC-gated (Owner/Accounting); a 403 is expected for
      // other roles. We still log so unexpected errors are diagnosable.
      if (err?.response?.status !== 403) {
        console.error('[ImportExport] failed to load import logs', err);
      }
    }
  };
  useEffect(() => { loadLogs(); }, []);

  const runPreview = async () => {
    if (!file) { toast.error('Pilih file dulu'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/import/preview/${importType}`, fd);
      setPreview(data);
      toast.success(`${data.total} baris terdeteksi`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal'); }
  };

  const runImport = async () => {
    if (!file) { toast.error('Pilih file dulu'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    setImporting(true);
    setResult(null);
    try {
      const { data } = await api.post(`/import/${importType}`, fd);
      setResult(data);
      const msg = data.header_row
        ? `Header di baris ${data.header_row}. Berhasil import ${data.success}/${data.total}`
        : `Berhasil import ${data.success}/${data.total}`;
      if (data.failed > 0) toast.warning(msg);
      else toast.success(msg);
      loadLogs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal import'); }
    finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="import-export-page">
      <Tabs defaultValue="import">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="import" data-testid="tab-import">Import Data</TabsTrigger>
          <TabsTrigger value="export" data-testid="tab-export">Export Data</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">Backup</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Log Import</TabsTrigger>
        </TabsList>

        <TabsContent value="import">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-sm font-heading">Bulk Import CSV / Excel</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Tipe Data</Label>
                  <Select value={importType} onValueChange={setImportType}>
                    <SelectTrigger data-testid="import-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {IMPORT_TYPES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="create">Create (buat baru saja)</SelectItem>
                      <SelectItem value="update">Update (yang sudah ada)</SelectItem>
                      <SelectItem value="upsert">Upsert (create / update)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="secondary" className="w-full" onClick={() => {
                    const t = IMPORT_TYPES.find(x => x.key === importType);
                    downloadFile(`/import/templates/${importType}`, t?.file || `template-${importType}.xlsx`);
                  }} data-testid="download-template-button"><FileDown className="h-4 w-4 mr-2" />Unduh Template</Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Pilih File (.csv / .xlsx)</Label>
                <Input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file-input" />
              </div>

              <div className="flex gap-2">
                <Button variant="secondary" onClick={runPreview} disabled={!file} data-testid="preview-button">Preview</Button>
                <Button onClick={runImport} disabled={!file || importing} data-testid="import-button"><Upload className="h-4 w-4 mr-2" />{importing ? 'Memproses…' : 'Import Sekarang'}</Button>
              </div>

              {preview && (
                <Card className="border-border bg-background/40">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Preview Data ({preview.total} baris)</CardTitle>
                    {preview.header_row ? (
                      <div className="text-xs text-muted-foreground mt-1">
                        Header terdeteksi di baris <span className="font-semibold text-foreground">{preview.header_row}</span>
                        {preview.data_start_row ? ` · Data mulai baris ${preview.data_start_row}` : ''}
                        {preview.sheet ? ` · Sheet "${preview.sheet}"` : ''}
                        {preview.skipped_empty > 0 ? ` · ${preview.skipped_empty} baris kosong dilewati` : ''}
                      </div>
                    ) : (
                      <div className="text-xs text-orange-300 mt-1">
                        ⚠️ Header tidak terdeteksi. Pastikan file memiliki kolom yang dibutuhkan (misal: Product Name / Nama Produk).
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5">
                        <tr>
                          {preview.headers.map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.preview.map((row, i) => {
                          // Build a stable, content-derived key so React's
                          // reconciler does not corrupt cell state when the
                          // preview list mutates (sorting, re-imports, etc.).
                          // Falls back to the row index only when no usable
                          // identifying field exists.
                          const idLike = row.id || row.code || row.email || row.invoice_number || row.client_name || row.product_name || row.package_name;
                          const rowKey = idLike ? `r-${String(idLike)}-${i}` : `idx-${i}`;
                          return (
                            <tr key={rowKey} className="border-t border-border">
                              {preview.headers.map(h => <td key={h} className="px-2 py-1">{row[h] ?? ''}</td>)}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {result && (
                <Card className={`border-border ${result.failed > 0 ? 'bg-orange-500/5' : 'bg-emerald-500/5'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {result.failed > 0 ? <AlertTriangle className="h-5 w-5 text-orange-300" /> : <CheckCircle2 className="h-5 w-5 text-emerald-300" />}
                      <div className="flex-1">
                        <div className="font-semibold text-sm">Hasil Import</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {result.header_row ? `Header ditemukan di baris ${result.header_row}. ` : ''}
                          {result.total} baris data terdeteksi
                          {result.skipped_empty > 0 ? ` (${result.skipped_empty} baris kosong dilewati)` : ''}
                          .{' '}
                          <span className="text-emerald-300 font-medium">Berhasil {result.success}</span>
                          {' · '}
                          <span className={result.failed > 0 ? 'text-red-300 font-medium' : ''}>Gagal {result.failed}</span>
                          .
                        </div>
                      </div>
                    </div>
                    {result.errors?.length > 0 && (
                      <div className="mt-3 max-h-40 overflow-y-auto text-xs space-y-1 rounded-md bg-card/40 p-2">
                        {result.errors.slice(0, 50).map((err, i) => {
                          // Row number + error message gives a stable per-error
                          // key while still allowing distinct duplicate-row
                          // reports to render side-by-side.
                          const errKey = `e-${err.row ?? 'na'}-${(err.error || '').slice(0, 40)}-${i}`;
                          return (
                            <div key={errKey} className="text-red-300">Baris {err.row}: {err.error}</div>
                          );
                        })}
                        {result.errors.length > 50 ? (
                          <div className="text-muted-foreground">…dan {result.errors.length - 50} error lainnya</div>
                        ) : null}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="export">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-sm font-heading">Ekspor Data</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {EXPORT_OPTIONS.map(o => (
                  <Card key={o.key} className="bg-background/40 border-border">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <div className="font-medium">{o.label}</div>
                        <div className="text-xs text-muted-foreground">Format: Excel (.xlsx)</div>
                      </div>
                      <Button variant="secondary" onClick={() => downloadFile(o.url, o.file)} data-testid={`export-${o.key}`}><FileSpreadsheet className="h-4 w-4 mr-2" />Unduh</Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="backup">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-sm font-heading">Full Backup</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold">Unduh Backup Lengkap</div>
                  <div className="text-sm text-muted-foreground">Satu file Excel berisi Clients, Quotations, Quotation Items, Invoices, Invoice Items, Payments, Receivables, dan Follow Ups.</div>
                </div>
                <Button onClick={() => downloadFile('/export/backup', `backup-glowing-${new Date().toISOString().slice(0,10)}.xlsx`)} data-testid="backup-button"><FileDown className="h-4 w-4 mr-2" />Unduh Backup</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-sm font-heading">Riwayat Import</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Tanggal</TableHead><TableHead>Tipe</TableHead><TableHead>File</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Sukses</TableHead><TableHead className="text-right">Gagal</TableHead><TableHead>Oleh</TableHead></TableRow></TableHeader>
                <TableBody>
                  {logs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">Belum ada riwayat import.</TableCell></TableRow>}
                  {logs.map(log => (
                    <TableRow key={log.id} className="border-border">
                      <TableCell>{formatDateID(log.created_at)}</TableCell>
                      <TableCell>{log.import_type}</TableCell>
                      <TableCell className="text-muted-foreground">{log.file_name}</TableCell>
                      <TableCell className="text-right">{log.total_rows}</TableCell>
                      <TableCell className="text-right text-emerald-300">{log.success_rows}</TableCell>
                      <TableCell className="text-right text-red-300">{log.failed_rows}</TableCell>
                      <TableCell className="text-muted-foreground">{log.imported_by}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
